/**
 * The HubSpot lead form under the quiz result, and its hidden quiz fields.
 *
 * Hidden fields are set through the HubSpot forms API (not URL parameters)
 * when the form is ready, on every answer change and just before submit, so
 * answers can change without re-rendering the form or losing typed input.
 * The quiz's fallback values (e.g. a lead routing queue) fill fields the
 * HubSpot form leaves empty.
 *
 * - Legacy (v2) forms: hbspt.forms.create() callbacks.
 * - New editor (v4) forms: the .hs-form-frame rendered by the partial, the
 *   hs-form-event:* window events and form.setFieldValue().
 * - Preview (no form ID yet): the partial's preview form with plain hidden inputs.
 *
 * The v2 embed is loaded here when the page doesn't already have it
 * (loadV2Embed()); the v4 embed by em_quiz_enqueue_lead_form_script().
 *
 * @package ppd
 * @since   1.0.27
 */

import { el, waitFor } from './utils';

const valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

/**
 * Set an input's value in a way framework-controlled inputs notice.
 *
 * @param {HTMLInputElement} input
 * @param {string}           value
 */
function setInputValue(input, value) {
	if (input.value === value) {
		return;
	}
	valueSetter.call(input, value);
	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Load the legacy (v2) forms embed unless it's already on the page.
 *
 * functions/hubspot.php only loads it when a [hubspot] form scrolls into
 * view, so a page whose only form is the quiz's never gets it. The
 * data-em-hs-embed attribute stops that loader adding a second copy.
 *
 * @param {string} region HubSpot region, e.g. "na1".
 */
function loadV2Embed(region) {
	if ((window.hbspt && window.hbspt.forms) || document.querySelector('script[data-em-hs-embed], script[src*="hsforms.net/forms/embed/v2.js"]')) {
		return;
	}
	const host = !region || region === 'na1' ? 'js' : `js-${region}`;
	const script = document.createElement('script');
	script.src = `https://${host}.hsforms.net/forms/embed/v2.js`;
	script.setAttribute('data-em-hs-embed', '1');
	document.head.appendChild(script);
}

export default class LeadForm {
	/**
	 * @param {HTMLElement} container   The [data-em-quiz-form] element.
	 * @param {Object}      config      Quiz config.
	 * @param {Object}      callbacks
	 * @param {Function}    callbacks.onSubmitted
	 */
	constructor(container, config, { onSubmitted }) {
		this.container = container;
		this.lead = config.lead;
		this.preview = Boolean(config.preview);
		this.onSubmitted = onSubmitted;
		this.values = {};
		this.fallbacks = this.lead.fallbacks || {};
		this.mounted = false;
		this.formEl = null;
		this.v4Form = null;
		this.initialMarkup = container.innerHTML;

		if (!this.preview && this.lead.version !== 'v2') {
			this.listenV4();
		}
	}

	mount() {
		if (this.mounted) {
			return;
		}
		this.mounted = true;
		if (this.preview) {
			this.mountPreview();
		} else if (this.lead.version === 'v2') {
			this.mountV2();
		}
		// v4 renders itself from the markup; see listenV4().
	}

	/**
	 * @param {Object<string, string>} values Property name => value.
	 */
	sync(values) {
		this.values = values;
		this.apply();
	}

	apply() {
		// Quiz values win over fallbacks for the same property.
		const fallbacks = Object.keys(this.fallbacks).filter((name) => !(name in this.values));

		if (this.v4Form) {
			// v4 doesn't expose a hidden field's default here, so fallbacks are
			// always set (they replace a default set in HubSpot).
			const values = { ...this.fallbacks, ...this.values };
			Object.keys(values).forEach((name) => {
				try {
					this.v4Form.setFieldValue(`0-1/${name}`, values[name]);
				} catch (e) {
					console.warn(`[em-quiz] Could not set HubSpot field ${name}`, e);
				}
			});
		}
		if (this.formEl) {
			fallbacks.forEach((name) => this.setHidden(name, this.fallbacks[name], true));
			Object.keys(this.values).forEach((name) => this.setHidden(name, this.values[name]));
		}
		if (this.preview) {
			this.renderPreviewValues();
		}
	}

	/**
	 * Set a hidden field on the v2 or preview form.
	 *
	 * @param {string}  name
	 * @param {*}       value
	 * @param {boolean} [fallback] Only fill the field if it's empty.
	 */
	setHidden(name, value, fallback = false) {
		let input = this.formEl.querySelector(`input[name="${window.CSS.escape(name)}"]`);
		if (!input && this.preview) {
			input = el('input', { type: 'hidden', name });
			this.formEl.querySelector('[data-em-quiz-hidden-fields]').append(input);
		}
		if (!input) {
			console.warn(`[em-quiz] HubSpot form has no hidden field "${name}"`);
		} else if (!fallback || input.value === '') {
			setInputValue(input, String(value));
		}
	}

	/**
	 * Start a fresh form for a new quiz run.
	 */
	reset() {
		this.mounted = false;
		this.formEl = null;
		this.v4Form = null;
		this.values = {};
		// Restores the empty v2 container, the v4 frame or the preview form.
		// v4: the embed script should render a re-inserted frame; confirm in the HubSpot spike.
		this.container.innerHTML = this.initialMarkup;
	}

	mountPreview() {
		this.formEl = this.container.querySelector('[data-em-quiz-preview-form]');
		if (!this.formEl) {
			return;
		}
		this.formEl.addEventListener('submit', (event) => {
			event.preventDefault();
			if (!this.formEl.checkValidity()) {
				this.formEl.reportValidity();
				return;
			}
			this.apply();
			console.info('[em-quiz] Preview lead form submitted (nothing sent):', Object.fromEntries(new FormData(this.formEl)));
			this.onSubmitted();
		});
	}

	renderPreviewValues() {
		const list = this.container.querySelector('[data-em-quiz-hidden-list]');
		if (!list) {
			return;
		}
		const values = { ...this.fallbacks, ...this.values };
		list.replaceChildren(...Object.keys(values).flatMap((name) => [
			el('dt', { text: name }),
			el('dd', { text: String(values[name]) }),
		]));
	}

	mountV2() {
		const { portalId, formId, region } = this.lead;
		loadV2Embed(region);
		waitFor(() => window.hbspt && window.hbspt.forms && window.hbspt.forms.create)
			.then(() => {
				const unwrap = (form) => (form && form.jquery ? form[0] : form);
				window.hbspt.forms.create({
					region,
					portalId,
					formId,
					target: `#${this.container.id}`,
					onFormReady: (form) => {
						this.formEl = unwrap(form);
						this.apply();
					},
					onBeforeFormSubmit: (form) => {
						this.formEl = unwrap(form);
						this.apply();
					},
					onFormSubmitted: () => this.onSubmitted(),
				});
			})
			.catch(() => console.warn('[em-quiz] HubSpot forms script (v2) did not load'));
	}

	listenV4() {
		const matches = (event) => {
			const api = window.HubSpotFormsV4;
			const form = api && api.getFormFromEvent ? api.getFormFromEvent(event) : null;
			if (!form || (form.getFormId && form.getFormId() !== this.lead.formId)) {
				return null;
			}
			return form;
		};
		window.addEventListener('hs-form-event:on-ready', (event) => {
			const form = matches(event);
			if (form) {
				this.v4Form = form;
				this.apply();
			}
		});
		window.addEventListener('hs-form-event:on-submission:success', (event) => {
			if (matches(event)) {
				this.onSubmitted();
			}
		});
	}
}
